import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { Icon } from "@/components/Icon";
import { BomNode } from "@/features/job-tracking/queries/tracking-data/bom-hierarchy-generator";
import { PrioritySourceType } from "./purchasing-priority-utils";

/**
 * A component that conditionally displays a flag icon indicating that the purchasing priority of a BOM node has been
 * overridden by child nodes. Hovering over the icon will show a tooltip with details about the overrides.
 */
export const PurchasingPriorityOverrideFlag: React.FC<{
  sourceType: Exclude<PrioritySourceType, "default">;
  overriddenChildren: BomNode[];
}> = ({ sourceType, overriddenChildren }) => {
  // Render nothing if this node has no overridden children
  const numOverrides = overriddenChildren.length;
  if (!numOverrides) return null;

  // Use a highlighted icon color if any of the overridden children are job-specific
  const colorClass = { "text-primary": sourceType === "job" };

  // Define the tooltip that will be displayed on hover
  const toolTip = (
    <Tooltip>
      <div className="text-start">
        Overridden by {numOverrides.toFixed(0)} {numOverrides === 1 ? "child" : "children"}{" "}
        {sourceType === "job" ? "for" : "in"} {sourceType}:
        <ul className="ps-3 m-0">
          {overriddenChildren.map((child) => (
            <li key={child.item.id}>
              {child.item.name} (priority: {child.purchasingPriority.priority})
            </li>
          ))}
        </ul>
      </div>
    </Tooltip>
  );

  return (
    <OverlayTrigger overlay={toolTip}>
      <Icon icon="flag-fill" className={classNames("ms-2", colorClass)} />
    </OverlayTrigger>
  );
};
